import { useCallback, useState } from 'react';
import { z } from 'zod';

import { rage } from '~/rage';
import { Button } from '../../components/Button';
import {
	ButtonContainer,
	FormContainer,
	PanelContainer,
} from '../../components/Containers';
import { FormTitle } from '../../components/FormTitle';
import { TextInput } from '../../components/TextInput';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import {
	AuthTabs,
	setActiveTab,
	setConfirmTimer,
	setEmailConfirm,
} from '../../redux/slices/authTabsSlice';
import {
	IFormError,
	setEmail,
	setLoading,
	setLogin,
} from '../../redux/slices/formDataSlice';

const accountEmailSchema = z
	.string()
	.min(1, 'Почта обязательна для заполнения')
	.email('Некорректный формат почты');

const restoreSchema = z.object({
	login: z
		.string()
		.nonempty('Введите логин')
		.min(3, 'Логин должен состоять минимум из 3 символов')
		.max(60, 'Логин не может превышать 60 символов'),
	email: accountEmailSchema,
});

const Restore = () => {
	const [errors, setErrors] = useState<IFormError[]>([]);

	const email = useLayoutSelector((state) => state.formData.email);
	const login = useLayoutSelector((state) => state.formData.login);
	const loading = useLayoutSelector((state) => state.formData.loading);
	const dispatch = useLayoutDispatch();

	const handleSubmit = async () => {
		dispatch(setLoading(true));

		const errors: IFormError[] = [];
		try {
			await restoreSchema.parseAsync({
				email,
				login,
			});
			const result = await rage.accounts.restorePassword(email, login);
			dispatch(setEmail(result.maskedEmail));
			dispatch(setEmailConfirm('restore'));
			dispatch(setConfirmTimer(Math.floor(result.resendAvailableAt / 1000)));
			dispatch(setActiveTab(AuthTabs.ConfirmEmail));
		} catch (error) {
			if (error instanceof z.ZodError) {
				error.issues.forEach((err) => {
					if (err.path && typeof err.path[0] === 'string') {
						errors.push({ source: err.path[0], text: err.message });
					}
				});
			} else {
				rage.notifications.showException(error);
			}
		}

		setErrors(errors);
		dispatch(setLoading(false));
	};

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (errors !== null) {
				const { name, value } = event.target;
				if (name === 'email') {
					dispatch(setEmail(value));
				} else if (name === 'login') {
					dispatch(setLogin(value));
				}
				setErrors(errors.filter((error) => error.source !== name));
			}
		},
		[errors],
	);

	const handleLoginClick = () => {
		dispatch(setActiveTab(AuthTabs.Login));
	};

	return (
		<PanelContainer>
			<FormTitle text={'Восстановление доступа к аккаунту'} />
			<FormContainer>
				<TextInput
					name="login"
					placeholder="Введите логин"
					error={errors.find((error) => error.source === 'login')?.text}
					defaultValue={login}
					onChange={handleInputChange}
					tabIndex={1}
				/>
				<TextInput
					name="email"
					placeholder="Введите адрес электронной почты"
					error={errors.find((error) => error.source === 'email')?.text}
					defaultValue={email}
					onChange={handleInputChange}
					tabIndex={2}
				/>
			</FormContainer>
			<ButtonContainer>
				<Button
					primary
					name="restore"
					type="button"
					loading={loading}
					onClick={handleSubmit}
					tabIndex={3}
				>
					Отправить код на почту
				</Button>
				<Button
					name="return"
					disabled={loading}
					onClick={handleLoginClick}
					tabIndex={4}
				>
					Вернуться назад
				</Button>
			</ButtonContainer>
		</PanelContainer>
	);
};

export { Restore };
